import { useCallback, useEffect, useState } from 'react';
import { Button } from 'antd';
import { PageLayoutSidebar } from '../PageLayout';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import SidebarMenu from './SidebarMenu';
import { Link, useNavigate, useLocation, useMatch } from 'react-router-dom';
import { Icon } from '../primitives/icon/Icon';
import AccountSettingsTabs from './AccountSettingsTabs';
import { ModuleSwitcher } from './ModuleSwitcher';
import { DefaultSpaceRedirector } from './DefaultSpaceRedirector';
import ConnectTabs from './ConnectTabs';
import { useUserContext } from '@/contexts/UserContext';
import { useFeatureGate } from '@statsig/react-bindings';
import { useIsAdmin } from '@/hooks/useAuth';

// 12px outer padding (px-3 on PageLayoutSidebar, both sides) + 4px button
// padding + 36px Module button + 4px button padding + 12px outer padding
// = 68px total sidebar width; 44px is the inner content column width.
const SIDEBAR_COLLAPSED_WIDTH = 44;
const SIDEBAR_DEFAULT_WIDTH = 260;
const SIDEBAR_WIDE_WIDTH = 340;

const WIDTH_TRANSITION_MS = 300;

export function Sidebar() {
  const { sidebarCollapsed, setValue } = useUserContext();
  const isSettingsRoute = useMatch('/settings/*');
  const { value: connectPageEnabled } = useFeatureGate('connect_page');
  const isAdmin = useIsAdmin();
  const isConnectRouteMatch = useMatch('/connect/*');
  const isConnectRoute = connectPageEnabled && isAdmin && isConnectRouteMatch;
  const { search, pathname } = useLocation();
  const navigate = useNavigate();

  // Staggered reveal, symmetric in both directions:
  //
  // Expand: render SidebarMenu (top part: fold button, search, module
  // switcher) immediately with the tree hidden, so the top animates leftward
  // with the width transition. After 300ms, reveal the tree — its Collapse
  // panels + pill-shaped Menu items don't briefly reflow in the still-narrow
  // container.
  //
  // Collapse: hide the tree immediately, but keep SidebarMenu rendered during
  // the width shrink so the top section animates rightward→left in place.
  // After 300ms, swap to the CollapsedSidebar layout (icon-only module stack).
  const [treeReady, setTreeReady] = useState(!sidebarCollapsed);
  const [renderCollapsed, setRenderCollapsed] = useState(sidebarCollapsed);
  // Optional "wide" mode (260 → 340). Independent of collapse — the edge
  // handle toggles only this. Local state for now; promote to user context
  // if persistence is wanted. Declared above the collapse effect because
  // that effect also resets `wide` on collapse.
  const [wide, setWide] = useState(false);
  useEffect(() => {
    if (sidebarCollapsed) {
      setTreeReady(false);
      // Drop the widen-toggle on collapse so the next expand lands at the
      // default 260 instead of springing back to 340 — the wide state was
      // tied to "this editing session", not a persistent preference.
      setWide(false);
      const t = window.setTimeout(() => setRenderCollapsed(true), WIDTH_TRANSITION_MS);
      return () => window.clearTimeout(t);
    }
    setRenderCollapsed(false);
    const t = window.setTimeout(() => setTreeReady(true), WIDTH_TRANSITION_MS);
    return () => window.clearTimeout(t);
  }, [sidebarCollapsed]);
  const sidebarWidth = sidebarCollapsed
    ? SIDEBAR_COLLAPSED_WIDTH
    : wide
    ? SIDEBAR_WIDE_WIDTH
    : SIDEBAR_DEFAULT_WIDTH;

  const toggleSidebar = useCallback(
    (collapsed: boolean) => {
      setValue((prev) => ({ ...prev, sidebarCollapsed: collapsed }));
    },
    [setValue],
  );

  return (
    <PageLayoutSidebar className="relative text-xs text-content-primary pt-3 px-3 flex flex-col border-r border-[var(--color-antd-border)] bg-surface-sunken">
      {/* Mounted unconditionally so the default-space redirects fire even
          when the sidebar starts collapsed (or on the Settings/Connect
          panels) — see DefaultSpaceRedirector for details. */}
      <DefaultSpaceRedirector />
      <div style={{ width: sidebarWidth }} className="transition-all duration-300 overflow-hidden">
        {renderCollapsed ? (
          <CollapsedSidebar onExpand={() => toggleSidebar(false)} />
        ) : isSettingsRoute ? (
          <SettingsSidebar onCollapse={() => toggleSidebar(true)} />
        ) : isConnectRoute ? (
          <ConnectSidebar onCollapse={() => toggleSidebar(true)} />
        ) : (
          <SidebarMenu
            onSidebarOpenClose={toggleSidebar}
            sidebarOpen={!renderCollapsed}
            search={search}
            pathname={pathname}
            navigate={navigate}
            showTree={treeReady}
          />
        )}
      </div>
      {!sidebarCollapsed && <SidebarEdgeHandle wide={wide} onToggle={() => setWide((v) => !v)} />}
    </PageLayoutSidebar>
  );
}

/**
 * Thin clickable strip on the sidebar's right edge that toggles the sidebar
 * between default (260px) and wide (340px). The strip itself is invisible
 * until hover (subtle indigo tint); a small chevron pill anchors to the
 * border mid-height to telegraph the action. Hidden entirely when the
 * sidebar is collapsed — the Fold button handles that mode.
 */
function SidebarEdgeHandle({ wide, onToggle }: { wide: boolean; onToggle: () => void }) {
  // Hit target is 8px (was 4px). Anchored to the sidebar's right edge and
  // extending INWARD so the strip doesn't overlap page content past the
  // sidebar. Capped at 8px (not the WCAG 24px tap-target ideal) because
  // tree rows have hover-revealed `SiteEditMenu` / `BuildingEditMenu`
  // icons within the row's right side — a wider strip would intercept
  // those clicks via `z-10`. The bigger a11y win here is the
  // `:focus-visible` ring, which makes the affordance discoverable via
  // keyboard tab order regardless of width.
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={wide ? 'Shrink sidebar' : 'Widen sidebar'}
      title={wide ? 'Shrink sidebar' : 'Widen sidebar'}
      className="group absolute right-0 top-0 h-full w-2 cursor-pointer z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-inset"
    >
      {/* Visible hover tint: rightmost 4px of the hit zone (i.e. flush to
          the sidebar's right edge). */}
      <span
        aria-hidden
        className="absolute right-0 top-0 h-full w-1 bg-transparent group-hover:bg-gray-900/10 transition-colors"
      />
      <span
        aria-hidden
        className="absolute right-[-14px] top-1/2 -translate-y-1/2 flex h-7 w-[14px] items-center justify-center rounded-r-md bg-gray-900 text-white opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity shadow-sm"
      >
        {wide ? (
          <LeftOutlined style={{ fontSize: 9 }} />
        ) : (
          <RightOutlined style={{ fontSize: 9 }} />
        )}
      </span>
    </button>
  );
}

/**
 * Sidebar contents when collapsed: fold/unfold button + icon-only module
 * switcher. The header row centres a 32px circle button; a 9px spacer
 * below it replaces the expanded state's `pb-2` gap so ModuleSwitcher
 * lands at the same Y as in the expanded state — no visible jump on
 * toggle.
 *
 * Width comes from the parent (`SIDEBAR_COLLAPSED_WIDTH = 44`), so this
 * component doesn't fix its own width. Total sidebar width is 12 (outer
 * px-3) + 44 (this column) + 12 (outer px-3) = 68px.
 */
function CollapsedSidebar({ onExpand }: { onExpand: () => void }) {
  return (
    <>
      {/* Header row: 32px button, no pb-2 here. The divider below replaces
          the pb-2 gap with the same 8px of vertical space so ModuleSwitcher
          lands at the same Y as in the expanded state (no jump on toggle). */}
      <div className="flex justify-center items-center">
        <Button
          type="text"
          shape="circle"
          icon={<MenuUnfoldOutlined />}
          onClick={onExpand}
          aria-label="Expand sidebar"
        />
      </div>
      {/* Spacer preserving the previous divider's vertical footprint (9px)
          so ModuleSwitcher lands at the same Y as in the expanded state. */}
      <div className="h-[9px]" />
      <ModuleSwitcher collapsed />
    </>
  );
}

function ConnectSidebar({ onCollapse }: { onCollapse: () => void }) {
  return (
    <>
      <div className="flex items-center justify-between">
        <Link to="/" className="btn btn-primary flex ml-4 text-lg font-light">
          <Icon name="left" className="mr-3 mt-1"></Icon> <span className="">Back</span>
        </Link>
        <MenuFoldOutlined
          className="shrink-0 text-content-primary cursor-pointer mr-3"
          style={{ fontSize: 16 }}
          onClick={onCollapse}
        />
      </div>
      <div className="flex mb-8 ml-6 my-8 text-content-primary text-sm">
        <Icon name="link" className="h-4 w-4 mt-[2px]" />
        <span className="ml-2">Connect</span>
      </div>
      <div className="mt-8 flex">
        <ConnectTabs />
      </div>
    </>
  );
}

function SettingsSidebar({ onCollapse }: { onCollapse: () => void }) {
  return (
    <>
      <div className="flex items-center justify-between">
        <Link to="/" className="btn btn-primary flex ml-4 text-lg font-light">
          <Icon name="left" className="mr-3 mt-1"></Icon> <span className="">Back</span>
        </Link>
        <MenuFoldOutlined
          className="shrink-0 text-content-primary cursor-pointer mr-3"
          style={{ fontSize: 16 }}
          onClick={onCollapse}
        />
      </div>
      <div className="flex mb-8 ml-6 my-8 text-content-primary text-sm">
        <Icon name="gear" className="h-4 w-4 mt-[2px]" />
        <span className="ml-2">Account Settings</span>
      </div>
      {/* No `bg-surface` on this wrapper — the tabs sit directly on the
        sidebar's grey background, matching the rest of the sidebar chrome
        instead of floating in a white card. */}
      <div className="mt-8 flex">
        <AccountSettingsTabs />
      </div>
    </>
  );
}
